import React, { useCallback, useEffect, useMemo, useState } from "react";
import LargePopupModalContainer from "../Containers/LargePopupModalContainer";
import "../../css/GlobalStyles.css";
import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import {
  FormInputType,
  NotificationType,
  SavedFormQuestion,
  SavedFormQuestionState,
  ThemeType,
} from "@markit/common.types";
import { DarkTheme, LightTheme, ThemeStyle } from "../../hooks/useTheme";
import FormBuilder from "./FormBuilder";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../redux/store";
import {
  LoginState,
  accountActions,
  updateProfileQuestions,
} from "../../redux/slices/accountSlice";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import CustomCheckbox from "../CustomCheckbox";
import {
  filterUndefinedValues,
  sortSavedQuestions,
} from "@markit/common.utils";
import SavedQuestionItem from "../DisplayItem/DataQuestion/SavedQuestionItem";
import AlertContainer from "../Containers/AlertContainer";
import SearchBoxContainer from "../Containers/SearchBoxContainer";
import filter from "lodash.filter";
import LargePopupModalHeader from "../Containers/LargePopupModalHeader";
import RectangleButton from "../Buttons/RectangleButton";
import { API } from "../../API";
import { CircularProgress } from "@mui/material";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import { showNotificationBanner } from "../../utils/notificationUtils";
import SavedQuestionTypeDropdown, {
  DataQuestionFilterType,
} from "./SavedQuestionTypeDropdown";
import { getSavedFormQuestionsToDisplay } from "../../utils/eventUtils/formUtils";
import ListTabs from "../DropdownsAndTabs/ListTabs";
import EmptyStateButton from "../Buttons/EmptyStateButton";

export enum FormSavedQuestionsType {
  DATA = "Data", // Top level data questions panel
  EVENT = "Event",
  PROFILE = "Profile",
  ASSIGN = "Assign", // For spreadsheet assign columns, diff logic since can only select one question at a time
}

// assignOnPress, selectedAssignedQuestion, newQuestionOptions -> spreadsheet assign columns
type FormSavedQuestionsProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  type: FormSavedQuestionsType;
  assignOnPress?: (question: SavedFormQuestion) => boolean;
  selectedAssignedQuestion?: SavedFormQuestion;
  newQuestionOptions?: string[];
  eventId?: string;
  theme?: ThemeStyle;
};

const FormSavedQuestions = (props: FormSavedQuestionsProps) => {
  const {
    isVisible,
    setIsVisible,
    type,
    assignOnPress,
    selectedAssignedQuestion,
    newQuestionOptions,
    eventId,
    theme,
  } = props;
  const { currentEventFormQuestions, savedQuestions, accountData, loggedIn } =
    useSelector((state: AppState) => state.account);
  const dispatch = useDispatch();
  const [newFormVisible, setNewFormVisible] = useState(false);
  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);
  const [selectedFormQuestions, setSelectedFormQuestions] = useState<
    SavedFormQuestion[]
  >([]);
  const [formQuestionItem, setFormQuestionItem] = useState<
    SavedFormQuestion | undefined
  >();
  const [savedQuestionsToShow, setSavedQuestionsToShow] = useState<
    SavedFormQuestion[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [alertText, setAlertText] = useState("");
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [filterState, setFilterState] = useState(
    DataQuestionFilterType.ANY_TYPE
  );
  const [stateValue, setStateValue] = useState(0);

  useAsyncOnMount(async () => {
    // generate email and phone number saved questions if not in user's saved form questions
    if (loggedIn === LoginState.LOGGED_IN) {
      const emailExists = savedQuestions.some(
        (question) => question.formInput.inputType === FormInputType.EMAIL
      );

      if (!emailExists) {
        setLoadingDefaults(true);
        const { presetFormQuestions } =
          await API.user.generatePresetSavedFormQuestions({
            uid: accountData.uid,
          });
        if (presetFormQuestions.length > 0) {
          dispatch(accountActions.addSavedFormQuestions(presetFormQuestions));
        }
        setLoadingDefaults(false);
      }
    }

    // Preset the selected questions if selectedAssignQuestion is defined
    if (
      type === FormSavedQuestionsType.ASSIGN &&
      selectedAssignedQuestion &&
      selectedAssignedQuestion.formInput.label
    ) {
      setSelectedFormQuestions(
        selectedFormQuestions.concat(selectedAssignedQuestion)
      );
    }
  });

  const profileFormQuestions = useMemo(() => {
    const formQuestions = accountData.formQuestions.map((questionId) =>
      savedQuestions.find((question) => question.id === questionId)
    );
    const formQuestionsDefined: SavedFormQuestion[] =
      filterUndefinedValues(formQuestions);
    return formQuestionsDefined;
  }, [accountData.formQuestions, savedQuestions]);

  const currentFormQuestions = useMemo(
    () =>
      type === FormSavedQuestionsType.PROFILE
        ? profileFormQuestions
        : currentEventFormQuestions,
    [currentEventFormQuestions, profileFormQuestions, type]
  );

  const resetOnPress = useCallback(() => {
    setIsVisible(false);
    setSelectedFormQuestions([]);
    setFormQuestionItem(undefined);
  }, [setIsVisible]);

  const closeOnPress = useCallback(() => {
    if (selectedFormQuestions.length > 0) {
      setConfirmCloseVisible(true);
    } else {
      resetOnPress();
    }
  }, [resetOnPress, selectedFormQuestions.length]);

  const addOnPress = useCallback(() => {
    if (type === FormSavedQuestionsType.PROFILE) {
      // updated the newly selected form questions onProfileFollow to be true
      const updatedSelectedFormQuestions = selectedFormQuestions.map(
        (question) => ({
          ...question,
          onProfileFollow: true,
        })
      );
      // append the new selected form questions to the existing ones and replace any duplicates with the newer selected ones
      let newFormQuestions = profileFormQuestions;
      for (let i = 0; i < updatedSelectedFormQuestions.length; i++) {
        const index = newFormQuestions.findIndex(
          (question) => question.id === updatedSelectedFormQuestions[i].id
        );
        if (index !== -1) {
          newFormQuestions.splice(index, 1, updatedSelectedFormQuestions[i]);
        } else {
          newFormQuestions = newFormQuestions.concat(
            updatedSelectedFormQuestions[i]
          );
        }
      }

      dispatch(updateProfileQuestions(accountData.uid, newFormQuestions));
      resetOnPress();
      showNotificationBanner(
        dispatch,
        "Questions Added",
        NotificationType.AFFIRMATIVE
      );
    } else if (assignOnPress && type === FormSavedQuestionsType.ASSIGN) {
      const validAssign = assignOnPress(selectedFormQuestions[0]);
      if (!validAssign) {
        setAlertText(
          "This question has already been assigned to another column."
        );
        return;
      }
      resetOnPress();
    } else if (selectedFormQuestions.length > 0 && eventId) {
      // Add the eventId to the selected form questions when storing in currentEventFormQuestions
      const updatedSelectedFormQuestions = selectedFormQuestions.map(
        (question) => ({
          ...question,
          eventIds: [...question.eventIds, eventId],
        })
      );
      dispatch(
        accountActions.updateCurrentEventFormQuestions(
          updatedSelectedFormQuestions
        )
      );
      resetOnPress();
    } else {
      setAlertText("Please select a saved question to add first.");
    }
  }, [
    type,
    assignOnPress,
    selectedFormQuestions,
    eventId,
    profileFormQuestions,
    dispatch,
    accountData.uid,
    resetOnPress,
  ]);

  // we don't use selectedFormQuestions as a dependency so that it doesn't re-order while you're pressing options
  useEffect(() => {
    const finalToShow = sortSavedQuestions(
      savedQuestions,
      currentFormQuestions,
      selectedFormQuestions
    );
    setSavedQuestionsToShow(finalToShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFormQuestions, savedQuestions, newFormVisible, isVisible]);

  const contains = useCallback((question: SavedFormQuestion, query: string) => {
    const label = question.formInput.label ?? "";
    return label.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    setSearchTerm(text.toLowerCase());
  };

  const savedQuestionsToDisplay = useMemo(() => {
    let questions = getSavedFormQuestionsToDisplay(
      savedQuestionsToShow,
      stateValue === 0
        ? SavedFormQuestionState.ACTIVE
        : SavedFormQuestionState.INACTIVE,
      filterState
    );
    if (searchTerm !== "") {
      questions = filter(questions, (question: SavedFormQuestion) => {
        return contains(question, searchTerm);
      });
    }
    return questions;
  }, [contains, filterState, savedQuestionsToShow, searchTerm, stateValue]);

  const selectQuestionOnPress = useCallback(
    (isSelectedOption: boolean, question: SavedFormQuestion) => {
      if (isSelectedOption) {
        setSelectedFormQuestions(
          selectedFormQuestions.filter((q) => q.id !== question.id)
        );
      } else {
        setSelectedFormQuestions(
          type === FormSavedQuestionsType.ASSIGN &&
            selectedFormQuestions.length > 0
            ? [question]
            : selectedFormQuestions.concat(question)
        );
      }
    },
    [selectedFormQuestions, type]
  );

  const newQuestionOnPress = useCallback(() => {
    setNewFormVisible(true);
  }, []);

  return (
    <>
      <LargePopupModalContainer
        showModal={isVisible}
        headerComp={
          <LargePopupModalHeader
            backOnPress={closeOnPress}
            altHeaderLeft={"mdi:close"}
            title={
              type === FormSavedQuestionsType.ASSIGN
                ? "Assign Column"
                : "Data Questions"
            }
            theme={theme}
            headerRight={
              <RectangleButton
                buttonLabel={
                  type === FormSavedQuestionsType.ASSIGN &&
                  !selectedAssignedQuestion
                    ? "Assign"
                    : "Save"
                }
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                disabled={
                  selectedFormQuestions.length === 0 &&
                  !selectedAssignedQuestion
                }
                onPress={addOnPress}
                altPaddingHorz={14}
                altPaddingVert={10}
                altBorderRadius={100}
                theme={theme}
              />
            }
          />
        }
        valueComp={
          <div
            className="HideScrollbar"
            style={{ overflowY: "scroll", height: "calc(100vh - 120px)" }}
          >
            <div
              className="ColumnNormal"
              style={{ gap: 14, paddingBottom: 14 }}
            >
              {type === FormSavedQuestionsType.ASSIGN ? (
                <div className="ColumnNormal" style={{ gap: 7 }}>
                  <span style={{ fontSize: 14, color: Colors.GRAY1 }}>
                    Assign this column to an existing question field or create a
                    new one.
                  </span>
                </div>
              ) : null}
              <div className="AlignedRow" style={{ gap: 10 }}>
                <SearchBoxContainer
                  placeholder="Search Questions..."
                  onChange={(e) => handleSearch(e.target.value)}
                  containerStyles={{ ...theme?.NewPrimaryBG, marginTop: 0 }}
                  inputStyles={theme?.PrimaryText}
                />
                <SavedQuestionTypeDropdown
                  selectedType={filterState}
                  setSelectedType={setFilterState}
                  themeType={
                    theme === LightTheme || !theme
                      ? ThemeType.Light
                      : ThemeType.Dark
                  }
                />
                <RectangleButton
                  buttonLabel="New"
                  onPress={newQuestionOnPress}
                  iconRight={
                    <Icon
                      icon="ion:add-circle"
                      height={18}
                      style={{ color: Colors.BLUE5 }}
                    />
                  }
                  altColor={
                    theme ? theme.NewPrimaryBG.backgroundColor : Colors.WHITE1
                  }
                  altTextColor={Colors.BLUE5}
                  width={80}
                  containerStyles={{
                    border: `0.5px solid ${Colors.GRAY2}`,
                    paddingInline: 14,
                    paddingBlock: 12,
                    borderRadius: 10,
                  }}
                />
              </div>
              <ListTabs
                tabLabels={["Active", "Inactive"]}
                tabNumbers={[-1, -1]}
                selectedValue={stateValue}
                onChange={setStateValue}
                altColor={theme ? theme.ButtonBG.backgroundColor : Colors.BLACK}
                tabStyles={{
                  backgroundColor:
                    theme === DarkTheme ? Colors.GRAY7 : undefined,
                }}
                newTextColor={theme === DarkTheme ? Colors.WHITE : Colors.BLACK}
                newSelectedTextColor={
                  theme ? theme.ButtonText.color : Colors.WHITE
                }
              />
            </div>
            {!loadingDefaults ? (
              savedQuestionsToDisplay.length > 0 ? (
                <div className="ColumnNormal" style={{ paddingBottom: 100 }}>
                  {savedQuestionsToDisplay.map((question) => {
                    const currentlySelected = currentFormQuestions.some(
                      (q) => q.id === question.id
                    );
                    const isSelectedOption =
                      selectedFormQuestions.some((q) => q.id === question.id) ||
                      currentlySelected;
                    return (
                      <div key={question.id}>
                        <SavedQuestionItem
                          item={question}
                          onItemPress={() =>
                            selectQuestionOnPress(isSelectedOption, question)
                          }
                          rightIcon={
                            <div className="AlignedRow" style={{ gap: 5 }}>
                              <CustomCheckbox
                                checked={isSelectedOption}
                                onChange={() =>
                                  selectQuestionOnPress(
                                    isSelectedOption,
                                    question
                                  )
                                }
                                sx={{
                                  padding: 0,
                                  pointerEvents: currentlySelected
                                    ? "none"
                                    : "all",
                                  opacity: currentlySelected ? 0.4 : 1,
                                }}
                              />
                            </div>
                          }
                          disabled={currentlySelected}
                          theme={theme}
                          editOnPress={() => {
                            setNewFormVisible(true);
                            setFormQuestionItem(question);
                          }}
                        />
                        <hr style={{ ...theme?.DividerColor }} />
                      </div>
                    );
                  })}
                  <RectangleButton
                    buttonLabel="New Question"
                    iconRight={
                      <Icon
                        icon="ion:add-circle"
                        height={18}
                        style={{ color: Colors.BLUE5 }}
                      />
                    }
                    onPress={newQuestionOnPress}
                    altColor={
                      theme?.NewPrimaryBG.backgroundColor || Colors.WHITE1
                    }
                    altTextColor={Colors.BLUE5}
                    containerStyles={{
                      paddingBlock: 12,
                      paddingInline: 30,
                      border: `0.5px solid ${
                        theme?.EditableTextFieldBorder.borderColor ||
                        Colors.GRAY2
                      }`,
                    }}
                  />
                </div>
              ) : (
                <EmptyStateButton
                  title={
                    searchTerm !== ""
                      ? "No Results Found"
                      : stateValue === 0
                      ? "No Active Questions"
                      : "No Inactive Questions"
                  }
                  icon={
                    <Icon
                      icon={searchTerm !== "" ? "ion:search" : "mdi:database"}
                      height={60}
                      style={{
                        color: theme ? theme.PrimaryText.color : Colors.BLACK,
                      }}
                    />
                  }
                  iconBox={70}
                  containerStyles={{ paddingTop: 100 }}
                  iconBackgroundColor={
                    theme === DarkTheme ? Colors.GRAY7 : undefined
                  }
                  theme={theme}
                />
              )
            ) : (
              <div className="Centering">
                <CircularProgress style={{ color: Colors.GRAY1 }} size={24} />
              </div>
            )}
          </div>
        }
        theme={theme}
      />
      {newFormVisible ? (
        <FormBuilder
          type={type}
          selectedQuestions={selectedFormQuestions}
          setSelectedQuestions={(questions: SavedFormQuestion[]) => {
            if (type === FormSavedQuestionsType.ASSIGN) {
              selectQuestionOnPress(false, questions[questions.length - 1]);
            } else {
              setSelectedFormQuestions(questions);
            }
          }}
          setIsFormsModalOpen={setNewFormVisible}
          formQuestionItem={formQuestionItem}
          setFormQuestionItem={setFormQuestionItem}
          newQuestionOptions={newQuestionOptions}
          theme={theme}
        />
      ) : null}
      <ConfirmDeleteModal
        heading="Are you sure you want to exit?"
        subtext="Your selections won't be saved. Are you sure you want to exit?"
        deleteButtonText="Exit"
        hideModal={!(confirmCloseVisible && selectedFormQuestions.length > 0)}
        setIsVisible={setConfirmCloseVisible}
        deleteOnPress={() => {
          setConfirmCloseVisible(false);
          resetOnPress();
        }}
        icon={<Icon icon="ion:exit-outline" height={40} />}
        theme={theme}
      />
      <AlertContainer
        headerComp={alertText}
        theme={theme}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
    </>
  );
};

export default FormSavedQuestions;
